"use client";
import { useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import clsx from "clsx";
import pom from "@/utils/pom";
import { Category } from "@/dto/category";
import { Merchant } from "@/dto/merchant";
import { DayPart } from "@/dto/dayPartMenu";
import { applyDataTestId } from "@/utils/testUtils";
import { HeroCtaSpecial } from "@/constants/enum";
import { ROUTE_MENU_ALL, ROUTE_RETAIL } from "@/utils/constants";
import { HardNavigationLink } from "@/components/hardNavigationLink/hardNavigationLink";
import { shouldHardNavigate } from "@/utils/hardNavigation";

export interface HeroSectionProps {
  merchant: Merchant;
  categoriesState: any[];
  merchantColor: string;
  dayPart?: DayPart | null;
  restaurantMfeEnabled?: boolean;
}

export const HeroSection = ({
  merchant,
  categoriesState,
  merchantColor,
  dayPart,
  restaurantMfeEnabled,
}: HeroSectionProps) => {
  const merchantMeta = merchant.merchantMeta;
  const heroImageUrl = merchantMeta.welcomeGcs ?? "/DefaultHeroBanner.jpg";
  const heroHeaderText = merchantMeta.welcomeMessage ?? "Default Header Text";
  const heroButtonText = merchantMeta.ctaButtonText ?? "Default Button Text";
  const welcomeMsg = merchantMeta.welcomeMessage?.trim();
  const textContainerDirection = "center";
  const heroButtonCategory = useMemo(
    () =>
      categoriesState?.find(
        (cat: Category) => cat.id === merchantMeta?.heroCtaDestination
      ),
    [merchantMeta, categoriesState]
  );
  const buttonRoute = heroButtonCategory?.name || "all";
  const isServiceMerchant = merchant.isService;
  const isRestaurantMerchant = merchant.isRestaurant;
  const isEssentialMerchant = merchant.isEssentialMerchant;

  const getOppositeDirection = (direction: string) => {
    switch (direction) {
      case "top":
        return "bottom";
      case "bottom":
        return "top";
      case "left":
        return "right";
      case "right":
        return "left";
      default:
        return direction;
    }
  };

  const oppositeDirection = getOppositeDirection(textContainerDirection);
  const textContainerDirectionClass = clsx({
    "top-1/2": oppositeDirection === "top",
    "bottom-1/2": oppositeDirection === "bottom",
    "left-1/2": oppositeDirection === "left",
    "right-1/2": oppositeDirection === "right",
  });

  const getButtonHref = () => {
    if (isServiceMerchant) {
      return `/services/all#${buttonRoute}`;
    }

    if (isRestaurantMerchant) {
      if (merchantMeta?.heroCtaDestination === HeroCtaSpecial.ABOUT) {
        return "/about";
      }

      const hash = buttonRoute === "all" ? "" : `#${buttonRoute}`;
      let route = ROUTE_MENU_ALL(
        restaurantMfeEnabled ?? false
      );

      if (dayPart?.formattedName) {
        const queryParams = new URLSearchParams();
        queryParams.set("menu", dayPart.formattedName);
        route += `?${queryParams.toString()}`;
      }

      return `${route}${hash}`;
    }

    if (isEssentialMerchant) {
      const hash = buttonRoute === "all" ? "" : `#${buttonRoute}`;
      return `/categories/all${hash}`;
    }

    if (
      process.env.NEXT_PUBLIC_ALLOW_SHOP_AS_DEFAULT_RETAIL_ROUTE === "true" &&
      merchant.isRetail
    ) {
      return buttonRoute === "all"
        ? ROUTE_RETAIL
        : `${ROUTE_RETAIL}/${buttonRoute}`;
    }

    return `/${buttonRoute}`;
  };

  const buttonHref = getButtonHref();
  const ButtonLink = shouldHardNavigate(buttonHref, merchant)
    ? HardNavigationLink
    : Link;

  return (
    <div
      data-testid={applyDataTestId(pom.landingPage.heroSection)}
      className="flex flex-col items-center pt-6 sm:px-8"
    >
      <div
        className={[
          "flex",
          "justify-center",
          "items-center",
          "h-[500px]",
          "overflow-hidden",
          "relative",
          "w-full",
          "sm:rounded-lg",
        ].join(" ")}
      >
        <div
          className={[
            "z-[5]",
            "absolute",
            "rounded-lg",
            "bg-transparent",
            "p-9",
            textContainerDirectionClass,
            "lg:w-[36%]",
            "lg:right-[55%]",
          ].join(" ")}
        >
          <h2
            data-testid={applyDataTestId(pom.landingPage.heroHeadline)}
            className={"text-white text-3xl"}
          >
            {heroHeaderText ?? "Default Header Text"}
          </h2>
          <ButtonLink
            href={buttonHref}
            data-testid={applyDataTestId(pom.landingPage.heroSectionButton)}
            className={[
              "cursor-pointer",
              "block",
              "text-center",
              "w-full",
              "border-none",
              "mt-5",
              "truncate",
              "bg-white",
              "text-[#1F2A37] px-5 py-2.5 gap-2 rounded-lg font-medium text-sm leading-[21px]",
              "sm:min-w-[360px]",
            ].join(" ")}
            style={{ color: merchantColor }}
          >
            {heroButtonText}
          </ButtonLink>
        </div>
        <Image
          data-testid={applyDataTestId(pom.landingPage.heroImage)}
          className={[
            "absolute",
            "top-0",
            "left-0",
            "w-full",
            "h-full",
            "object-cover",
            "object-center",
          ].join(" ")}
          src={heroImageUrl || "/DefaultHeroBanner.jpg"}
          alt={welcomeMsg || "BackgroundHeroImage"}
          width="0"
          height="0"
          sizes="100vw"
          priority
        />
        <div className="absolute inset-0 bg-black opacity-40"></div>
      </div>
    </div>
  );
};
